import { useEffect, useRef, useState } from "react";
import { AdsType } from "@ads/shared";

/** Product-type options to upload into (the legacy "All Product Types" control). */
const OPTIONS: { type: AdsType; label: string }[] = [
  { type: AdsType.All, label: "All Product Types" },
  { type: AdsType.Newsletter, label: "Only Newsletters" },
  { type: AdsType.Event, label: "Only Events" },
  { type: AdsType.Survey, label: "Only Surveys" },
];

/**
 * The org-picker's "All Product Types" dropdown, repurposed as the **upload trigger**. It is
 * disabled until one or more organisations are selected; picking a product type specifies the
 * `adsType` and opens the ad create form for the selected orgs (it does not filter the list).
 */
export function UploadProductTypeMenu({
  disabled,
  onSelect,
}: {
  disabled: boolean;
  onSelect: (adsType: AdsType) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  return (
    <div className={`filter-dd upload-dd${disabled ? " is-disabled" : ""}`} ref={ref}>
      <button
        type="button"
        className="filter-dd-trigger"
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={open}
        title={disabled ? "Select one or more organisations first" : undefined}
        onClick={() => setOpen((o) => !o)}
      >
        All Product Types
        <span className="caret" aria-hidden>▾</span>
      </button>
      {open && !disabled && (
        <ul className="filter-dd-menu" role="menu">
          {OPTIONS.map((o) => (
            <li key={o.type} role="none">
              <button
                type="button"
                role="menuitem"
                className="filter-dd-item"
                onClick={() => {
                  setOpen(false);
                  onSelect(o.type); // specify the product type, then open the create form
                }}
              >
                {o.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
